import type { UserInstrumentClassificationDto, UserWithCustomerDto } from "@eu.mdotm.api/sphere";
import { animationProgress$, animationProgressDefault } from "$root/components/FakeAiLoader/atom";
import { eventBus } from "$root/event-bus";
import { dismissAllPlatformToasts } from "$root/notification-system/toast";
import { queryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import {
	reapplySphereMetricsBannerExpand$,
	reapplySphereMetricsBannerExpandDefault,
} from "$root/widgets-architecture/widgets/PortfolioCommentaryMDBlock";
import { resetSpawned } from "@mdotm/shadcn/legacy/react-extensions";
import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { Map } from "immutable";
import { type UserRole, guestUser, user$ } from ".";
import { isMustEnroll } from "./mfa";
import { crossIdentityBoundary } from "./identity-boundary";
import { initUserStorage, userStorage } from "./userStorage";
import {
	instrumentCustomisation$,
	instrumentCustomisationDefault,
} from "../instruments/Instrument-customisation/InstrumentCustomisationStore";
import { instrumentsRepository, tickersInfoPool$, tickersInfoPoolDefault } from "../instruments/repository";
import { userClassificationsMap$ } from "../instruments/user-classifications";
import { notificationCenter$, notificationMarkerDefault } from "../notification-center/NotificationStore";
import { insightPanelSession$ } from "../portfolio-studio/insightPanelSession";
import { portfolioStudioDefault, portfolioStudioSettings$ } from "../portfolio-studio/PortfolioStudioStore";
import { accessToken$, impersonateEmail$, mfaEnrollmentToken$ } from "../session/atom";
import { beginSignout } from "./oidc";
import { userRepository } from "./repository";
import { defer } from "@mdotm/utils/runtime";
import { clamp } from "@mdotm/shadcn/legacy/utils";
import { MAX_PERCENT_DECIMAL_PLACES } from "$root/utils/monetary-math";

export function updateUser(userInfo: UserWithCustomerDto, impersonating = false) {
	writeAtom(user$, (user) => {
		// customer ceiling is server-authoritative (API field: decimalPreference); customers without a value lock users to 2.
		const customerDecimalPlacesPreferences = clamp(userInfo?.decimalPreference ?? 2, 2, MAX_PERCENT_DECIMAL_PLACES);
		return {
			...user,
			signedIn: true,
			id: userInfo.id!,
			email: userInfo.email!,
			availableServices: userInfo?.services || [],
			roles: (userInfo?.roles || []) as UserRole[],
			impersonating,
			customFields: userInfo?.customFields,
			name: userInfo?.name,
			surname: userInfo?.surname,
			customerName: userInfo?.customerName ?? "",
			importFormats: userInfo?.importFormats,
			exportFormats: userInfo?.exportFormats,
			importEnabled: userInfo?.importEnabled,
			exportEnabled: userInfo?.exportEnabled,
			customerId: userInfo?.customerId,
			// Customer-level ceiling for Sphere AI's web search. Absent reads as
			// withheld, matching how sphere-mcp defaults the same field (SECURITY.md
			// rule 1 there) — so a backend that does not serve it yet hides the switch
			// rather than showing one whose requests the server would refuse.
			customerWebSearchEnabled: userInfo?.customerWebSearchEnabled ?? false,
			customerSmartXEnabled: userInfo?.customerSmartXEnabled ?? false,
			// Resolved server-side from the user's IdP MFA settings. A missing field
			// leaves the enrollment gate closed (see `needsMfaEnrollment`).
			mfaStatus: userInfo?.mfaStatus,
			customerDecimalPlacesPreferences,
			// userDecimalPlacesPreferences is handled client side with optimistic update (API field: decimalPlaces).
			// guestUser has the field set to undefined, so upon login we set the value from userInfo or fall back to 2.
			// Subsequent updates won't overwrite it, but it is always re-clamped to the customer ceiling.
			userDecimalPlacesPreferences: clamp(
				user.userDecimalPlacesPreferences ?? (userInfo?.decimalPlaces || 2),
				2,
				customerDecimalPlacesPreferences,
			),
		};
	});
}

eventBus.addListener("user-update", ({ userInfo }) => {
	if (userInfo) {
		updateUser(userInfo);
	}
});

async function loadUserClassificationsMap(): Promise<void> {
	writeAtom(
		userClassificationsMap$,
		Map(
			await instrumentsRepository.allUserClassifications
				.fetchQuery()
				.then((cls): Array<[string, UserInstrumentClassificationDto]> => cls.map((c) => [c.classificationUuid!, c]))
				.catch(() => []),
		),
	);
}

export async function authSessionInit(accessToken: string): Promise<void> {
	// On reload the impersonation is restored from storage before this runs, so the
	// identity fetch must carry the impersonate header to resolve the target user
	// (all other authenticated calls below already get it via the interceptor).
	const impersonateEmail = readAtom(impersonateEmail$);
	const userInfo = impersonateEmail
		? await userRepository.infoImpersonated.fetchQuery(accessToken, impersonateEmail)
		: await userRepository.info.fetchQuery(accessToken);

	if (userInfo.id !== readAtom(user$).id) {
		cleanupSession();
	}

	// Forced MFA enrollment: hold the token out of `accessToken$` so no authenticated
	// side-channel (SSE, axios interceptor, queries) activates while the gate is up —
	// mirroring the backend rejecting every endpoint but `/mfa/enroll/*`. The enroll
	// calls read the token from `mfaEnrollmentToken$`; on verify success this function
	// runs again, resolves `ENROLLED`, and promotes the token below. User storage and
	// classifications are skipped — a gated user can only reach the enrollment screen.
	// Skipped while impersonating: the gate is about the real user's own device, and the
	// held token here is the impersonator's — nulling `accessToken$` would break them.
	if (isMustEnroll(userInfo.mfaStatus) && !impersonateEmail) {
		writeAtom(accessToken$, null);
		writeAtom(mfaEnrollmentToken$, accessToken);
		await defer(() => updateUser(userInfo, Boolean(impersonateEmail)));
		dismissAllPlatformToasts();
		return;
	}

	writeAtom(mfaEnrollmentToken$, null);
	writeAtom(accessToken$, accessToken);

	await initUserStorage();

	await loadUserClassificationsMap();

	await defer(() => updateUser(userInfo, Boolean(impersonateEmail)));

	dismissAllPlatformToasts();
}

/**
 * Re-pull the per-user data (userStorage, classifications, profile) after the
 * impersonation state has changed. The impersonator's `accessToken$` is left
 * untouched — only the `X-Impersonate-Email` header (already reflected in
 * `impersonateEmail$`) changes what the backend returns. `cleanupSession` keeps
 * `signedIn` true so the app never flashes the login screen during the swap.
 *
 * Only ever called from inside `crossIdentityBoundary`, and it reads and writes per-user state in
 * the plainest order because of it: nothing is mounted, so there is no observer to order any of it
 * for. Called outside one, an emptied store would be on screen between `cleanupSession` and the
 * read that refills it.
 */
async function reloadImpersonationSession(userInfo: UserWithCustomerDto, impersonating: boolean): Promise<void> {
	cleanupSession({ impersonate: true });
	await initUserStorage();
	await loadUserClassificationsMap();
	updateUser(userInfo, impersonating);
	dismissAllPlatformToasts();
}

/**
 * Start operating as `email` while keeping the impersonator's session/token.
 * Restores the previous impersonation state if the identity fetch fails, so a
 * bad email can never leave the app stuck sending a broken header.
 */
export async function startImpersonating(email: string): Promise<void> {
	const accessToken = readAtom(accessToken$);
	if (!accessToken) {
		throw new Error("Cannot impersonate without an active session");
	}
	const previous = readAtom(impersonateEmail$);
	await crossIdentityBoundary(async () => {
		writeAtom(impersonateEmail$, email);
		try {
			const userInfo = await userRepository.infoImpersonated.fetchQuery(accessToken, email);
			await reloadImpersonationSession(userInfo, true);
		} catch (error) {
			writeAtom(impersonateEmail$, previous);
			throw error;
		}
	});
}

/** Stop impersonating and return to the impersonator's own session. */
export async function stopImpersonating(): Promise<void> {
	await crossIdentityBoundary(async () => {
		writeAtom(impersonateEmail$, null);
		const accessToken = readAtom(accessToken$);
		if (!accessToken) {
			cleanupSession();
			return;
		}
		const userInfo = await userRepository.info.fetchQuery(accessToken);
		await reloadImpersonationSession(userInfo, false);
	});
}

export async function logout(opts: {
	/**
	 * Whether the credential is already gone — an expired session, or one that never finished
	 * hydrating. It decides whether the writes the app is still holding can be sent at all; see
	 * `crossIdentityBoundary`'s `settleOutgoing`.
	 *
	 * Required, not defaulted: every caller knows which of the two it is.
	 */
	credentialLost: boolean;
}): Promise<void> {
	await crossIdentityBoundary(
		async () => {
			writeAtom(accessToken$, null);
			writeAtom(mfaEnrollmentToken$, null);
			writeAtom(impersonateEmail$, null);
			writeAtom(user$, (currentUser) => ({ ...currentUser, signedIn: false }));
			dismissAllPlatformToasts();
			// Clears the OIDC user and redirects to the Cognito Hosted UI `/logout` endpoint.
			await beginSignout();
		},
		{ settleOutgoing: !opts.credentialLost },
	);
}

export function cleanupSession(opt?: { impersonate: boolean }): void {
	writeAtom(user$, opt?.impersonate ? { ...guestUser, signedIn: true } : { ...guestUser });
	writeAtom(instrumentCustomisation$, instrumentCustomisationDefault);
	writeAtom(portfolioStudioSettings$, portfolioStudioDefault);
	writeAtom(animationProgress$, animationProgressDefault);
	writeAtom(notificationCenter$, notificationMarkerDefault);
	writeAtom(reapplySphereMetricsBannerExpand$, reapplySphereMetricsBannerExpandDefault);
	writeAtom(tickersInfoPool$, tickersInfoPoolDefault);
	writeAtom(insightPanelSession$, null);
	writeAtom(userClassificationsMap$, null);
	writeAtom(mfaEnrollmentToken$, null);
	userStorage.reset();
	queryClient.clear();
	dismissAllPlatformToasts();
	resetSpawned();
}
